import { FC, ReactNode, useState } from 'react';
import { Modal, Table, Typography, Input, Button, cn } from '@crisellevaldez/speculo-ui';
import { Calendar, RotateCw, Search } from 'lucide-react';
import { formatDate } from '../../../utils/dateTime';
import { formatNumber } from '../../../utils/format';
import { STAT_CELL_TEXT } from '../../../utils/statsStyles';
import { OffsetPagination } from '../../common/OffsetPagination';
import { TableCard } from '../../common/TableCard';
import { TablePagination } from '../../common/TablePagination';
import { FilterButton } from '../../common/FilterButton';

interface Column<T> {
  key: string;
  header: ReactNode;
  cell?: (item: T) => ReactNode;
  sortable?: boolean;
  resizable?: boolean;
  width?: string;
  isPinned?: boolean;
  pinPosition?: string;
  isCentered?: boolean;
}

interface StatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  dateRange?: { from: Date | null; to: Date | null };
  onDateRangeChange?: (range: { from: Date | null; to: Date | null }) => void;
  customFilters?: ReactNode;
  /** How many filters are currently active — drives the colored badge on the
   *  mobile "Filters" button. Pass undefined or 0 to skip the badge. */
  activeFilterCount?: number;
  /** Called when the user hits "Reset". The modal renders a Reset control on
   *  desktop (inline, after customFilters) and in the mobile sheet footer
   *  whenever this prop is provided AND activeFilterCount > 0. */
  onResetFilters?: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<any>[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  isLoading?: boolean;
  totalCount?: number;
  filteredCount?: number;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  timezone?: string;
  // Built-in search — renders left of filter bar
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  // Offset pagination — when provided, replaces load-more
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number) => void;
  onPrevPage?: () => void;
  onNextPage?: () => void;
  onPageSizeChange?: (size: number) => void;
  paginationLabel?: string;
  // Row selection — when provided, renders a right-side detail panel
  onRowClick?: (item: Record<string, unknown>) => void;
  selectedRowId?: string;
  rightPanel?: ReactNode;
  // Legacy props kept for backward compatibility
  filterOptions?: { value: string; label: string }[];
  filterValue?: string;
  onFilterChange?: (value: string | string[]) => void;
  filterLabel?: string;
  showAllRecords?: boolean;
  onShowAllRecordsChange?: (checked: boolean) => void;
}

export const StatsModal: FC<StatsModalProps> = ({
  isOpen,
  onClose,
  title,
  dateRange,
  onDateRangeChange: _onDateRangeChange,
  customFilters,
  activeFilterCount = 0,
  onResetFilters,
  columns,
  data,
  isLoading,
  totalCount,
  filteredCount,
  hasMore,
  onLoadMore,
  onSort,
  timezone,
  onSearch,
  searchPlaceholder = 'Search...',
  currentPage,
  pageSize,
  onPageChange,
  onPrevPage,
  onNextPage,
  onPageSizeChange,
  paginationLabel,
  showAllRecords = false,
  onShowAllRecordsChange,
  onRowClick,
  selectedRowId,
  rightPanel,
}) => {
  const [internalSearch, setInternalSearch] = useState('');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);

  const hasFilterBar = onSearch || customFilters;
  const useOffsetPagination = currentPage !== undefined && pageSize !== undefined;
  const displayCount = filteredCount ?? totalCount;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full" className="mx-0 h-full">
      <Modal.Header>
        <div className="flex flex-col gap-0.5">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
            <Typography variant="h2">{title}</Typography>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-normal">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {dateRange?.from && dateRange?.to
                  ? `${formatDate(dateRange.from, timezone, 'MMM D, YYYY')} – ${formatDate(
                      dateRange.to,
                      timezone,
                      'MMM D, YYYY'
                    )}`
                  : 'All Time'}
              </span>
            </div>
          </div>
          {totalCount !== undefined && (
            <div className="text-xs text-muted-foreground">
              {formatNumber(totalCount)} {totalCount === 1 ? 'record' : 'records'}
            </div>
          )}
        </div>
      </Modal.Header>

      <Modal.Body className="flex-1 flex flex-col min-h-0">
        {/* Filter bar — flat (no card chrome), spans the modal.
            Desktop: search grows + customFilters inline at right.
            Mobile: search inline + a "Filters" button that opens a bottom
            modal with the customFilters stacked. */}
        {hasFilterBar && (
          <div className="w-full mb-4 shrink-0">
            {/* Desktop layout */}
            <div className="hidden md:flex items-center gap-2 w-full flex-wrap">
              {onSearch && (
                <div className="flex-1 min-w-[220px]">
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={internalSearch}
                    onChange={(e) => {
                      setInternalSearch(e.target.value);
                      onSearch(e.target.value);
                    }}
                    startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
                  />
                </div>
              )}
              {customFilters && (
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {customFilters}
                </div>
              )}
              {onResetFilters && activeFilterCount > 0 && (
                <Button
                  variant="outline"
                  className="p-2 justify-center shrink-0"
                  onClick={onResetFilters}
                  title="Reset filters"
                >
                  <RotateCw className="h-4 w-4" />
                </Button>
              )}
            </div>

            {/* Mobile layout — search inline, customFilters behind a button */}
            <div className="md:hidden flex items-center gap-2 w-full">
              {onSearch && (
                <div className="flex-1 min-w-0">
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={internalSearch}
                    onChange={(e) => {
                      setInternalSearch(e.target.value);
                      onSearch(e.target.value);
                    }}
                    startIcon={<Search className="h-4 w-4 text-muted-foreground" />}
                  />
                </div>
              )}
              {customFilters && (
                <FilterButton
                  onClick={() => setIsMobileFiltersOpen(true)}
                  activeCount={activeFilterCount}
                  label="Filters"
                  variant="outline"
                  className="shrink-0 px-3"
                />
              )}
            </div>
          </div>
        )}

        {/* Mobile filter sheet — renders the same customFilters stacked */}
        {customFilters && (
          <Modal
            isOpen={isMobileFiltersOpen}
            onClose={() => setIsMobileFiltersOpen(false)}
            size="sm"
          >
            <Modal.Header>
              <Typography variant="h3">Filters</Typography>
            </Modal.Header>
            <Modal.Body>
              <div className="space-y-4 [&_>*]:w-full [&_>div]:w-full">
                {customFilters}
              </div>
            </Modal.Body>
            <Modal.Footer>
              <div className="flex items-center justify-between w-full gap-2">
                {onResetFilters ? (
                  <Button
                    variant="outline"
                    onClick={onResetFilters}
                    disabled={activeFilterCount === 0}
                    className="inline-flex items-center gap-1.5"
                  >
                    <RotateCw className="h-4 w-4" />
                    Reset
                  </Button>
                ) : (
                  <span />
                )}
                <Button variant="primary" onClick={() => setIsMobileFiltersOpen(false)}>
                  Done
                </Button>
              </div>
            </Modal.Footer>
          </Modal>
        )}

        <div className="flex flex-1 min-h-0 gap-4 overflow-hidden">
          <TableCard
            className={cn(STAT_CELL_TEXT, rightPanel ? 'w-[60%]' : 'w-full')}
            footer={
              useOffsetPagination ? (
                <OffsetPagination
                  currentPage={currentPage!}
                  totalItems={displayCount ?? 0}
                  pageSize={pageSize!}
                  isLoading={isLoading}
                  label={paginationLabel}
                  onNextPage={onNextPage ?? (() => {})}
                  onPrevPage={onPrevPage ?? (() => {})}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                />
              ) : onLoadMore ? (
                <TablePagination
                  hasNext={!!hasMore}
                  onNextPage={onLoadMore}
                  isLoading={isLoading}
                  showAllRecordsOption={!!onShowAllRecordsChange}
                  showAllRecords={showAllRecords}
                  onShowAllRecordsChange={onShowAllRecordsChange}
                />
              ) : undefined
            }
          >
            <Table
              columns={columns}
              data={data}
              keyExtractor={(item) => item.messageId || item.activityId || item.logId || item.id}
              sortable={true}
              loading={isLoading}
              size="md"
              emptyStateText="No data available"
              className="w-full"
              onSort={(key, direction) => { if (onSort) onSort(key, direction); }}
              rowSelectable={!!onRowClick}
              selectedRowId={selectedRowId}
              onRowSelect={(id) => {
                if (!onRowClick || !id) return;
                const item = data.find((d) => (d.messageId || d.activityId || d.logId || d.id) === id);
                if (item) onRowClick(item);
              }}
            />
          </TableCard>
          {rightPanel && (
            <div className="w-[40%] rounded-xl border border-border bg-muted/30 overflow-y-auto scrollbar-custom">
              {rightPanel}
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
